import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';
import LockFilled from '@ant-design/icons/LockFilled';

import PurchaseFlow from '~components/Purchase/PurchaseFlow';
import type { Route } from '~components/Purchase/PurchaseFlow';
import RemainingTime from '~components/Purchase/RemainingTime';
import ShowSchedules from '~components/Purchase/ShowHeader/ShowSchedules';
import ShowSummary from '~components/Purchase/ShowHeader/ShowSummary';
import Layout from '~components/ui/Layout';
import OverlayMessage from '~components/ui/OverlayMessage/OverlayMessage';
import { OverlayMessageProps } from '~components/ui/OverlayMessage/OverlayMessage';
import { getFromAPI } from '~utils/api/common';
import useGlobalState from '~utils/hooks/useGlobalState';
import usePurchase from '~utils/hooks/usePurchase';
import useSettings from '~utils/hooks/useSettings.ts';
import normalizeSchedules from '~utils/normalizers/normalizeSchedule';
import normalizeShow from '~utils/normalizers/normalizeShow';

import { Show, ShowWithTickets } from '~types';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
const ROUTES = {
  inicio: {
    title: 'Comprar boletos',
  },
  pago: {
    title: 'Detalles del pago',
  },
  gracias: {
    title: 'Resumen de compra',
  },
};

type GSProps = {
  dates: string[];
  schedules: Show[];
  show: ShowWithTickets;
  bookingFee: number;
};

export const getStaticProps: GetStaticProps<GSProps> = async (context) => {
  const { session } = context?.params ?? {};

  try {
    const show = await getFromAPI<ShowWithTickets>(`/shows/${session}`);
    const dates = await getFromAPI(
      `/shows/dates?MovieId=${show.MovieId}&CinemaId=${show.CinemaId}`,
    );
    const date = dayjs(show.schedule)
      .tz(show?.Cinema?.timezone || 'America/Mazatlan')
      .startOf('day')
      .format();
    const schedules = await getFromAPI(
      `/shows/movie/${show.MovieId}?date=${date}&CinemaId=${show.CinemaId}`,
    );

    const settings = await getFromAPI('/settings');
    const { bookingFee } = settings;
    return {
      props: {
        show: normalizeShow(show) as ShowWithTickets,
        schedules: normalizeSchedules(schedules),
        dates: dates.map((item: { date: string }) => item.date),
        bookingFee,
      },
      revalidate: 60 * 60, // 1 minute
    };
  } catch (e) {
    if ((e as { statusCode: number })?.statusCode === 404) {
      return {
        notFound: true,
      };
    }
    throw e;
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

type Props = InferGetStaticPropsType<typeof getStaticProps>;

/**
 * TODO: Prevenir que el usuario mueva el state de la URL
 *
 * * Esta página solo se encarga de traer los datos iniciales y
 * * controlar el state de la url. Cada componente interno
 * * se encarga de su propio state.
 */
const PurchasePage: NextPage<Props> = ({ show, dates, schedules, bookingFee }) => {
  const [overlayProps, setOverlayProps] = useGlobalState<OverlayMessageProps>('OverlayMessage');
  const router = useRouter();
  const noRatedMessage = useSettings('noClasificationMovieDisclaimer');
  const [time, setTime] = useState(dayjs().tz(show?.Cinema?.timezone || 'America/Mazatlán'));
  const { sessionId, setPurchase, updatePurchase, resetPurchase } = usePurchase();
  const { session, step } = router.query;
  const currentStep = (step as Route) ?? 'inicio';
  const pageTitle = `${ROUTES[currentStep]?.title} | ${show?.Movie?.title} | ${show?.Cinema?.name}`;
  useEffect(() => {
    if (show?.Movie?.disclaimer) {
      setOverlayProps({
        title: 'Aviso Importante',
        icon: <InfoCircleOutlined className="text-5xl" />,
        visible: true,
        children: (
          <div className="max-w-[700px] text-center space-y-6">
            <p className="text-xl">{show.Movie.disclaimer}</p>
            <button className="btn text-black" onClick={() => setOverlayProps({})}>
              Aceptar
            </button>
          </div>
        ),
      });
    }
  }, [show?.Movie?.disclaimer]);

  useEffect(() => {
    if (show?.Movie?.rated === 'S/C' && noRatedMessage) {
      setOverlayProps({
        title: 'Aviso Importante',
        icon: <InfoCircleOutlined className="text-5xl" />,
        visible: true,
        children: (
          <div className="max-w-[700px] text-center space-y-6">
            <p className="text-xl">{noRatedMessage}</p>
            <button className="btn text-black" onClick={() => setOverlayProps({})}>
              Aceptar
            </button>
          </div>
        ),
      });
    }
  }, [show?.Movie?.rated, noRatedMessage]);

  useEffect(() => {
    if (!show?.Cinema?.onlineSale) {
      setOverlayProps({
        title: 'Venta en línea no disponible.',
        message: 'La venta en línea no esta disponible por el momento en este complejo',
        icon: <LockFilled className="text-5xl" />,
        visible: true,
        children: (
          <Link
            href="/"
            className="btn text-black"
            onClick={() => setTimeout(() => setOverlayProps({}), 500)}
          >
            Volver a la cartelera
          </Link>
        ),
      });
    }
    return () => {
      setOverlayProps({});
    };
  }, [show?.Cinema?.onlineSale]);

  useEffect(() => {
    const regExp = /\d+\?step=(inicio|pago|gracias)$/;
    if (router?.asPath && !regExp.test(router.asPath)) {
      const newPath = `${router.pathname
        .replace('[movie]', `${router?.query?.movie}`)
        .replace('[session]', `${router?.query?.session}`)}?step=inicio`;
      router.replace(newPath, undefined, { shallow: true });
    }
  }, [router?.asPath]);

  useEffect(() => {
    updatePurchase({ CinemaId: show?.CinemaId, sessionId: show?.sessionId, bookingFee });
  }, [sessionId]);

  useEffect(() => {
    if (`${sessionId}` !== session) {
      resetPurchase();
      setPurchase({ sessionId: parseInt(`${session}`, 10), seats: [], tickets: [] });
    }
  }, [session]);

  useEffect(() => {
    if (dayjs(show.schedule).isBefore(time.subtract(20, 'minutes'))) {
      const nextShow = schedules.find((item) =>
        dayjs(item.schedule).isAfter(time.subtract(20, 'minutes')),
      );
      const href = nextShow
        ? `/comprar-boletos/${show?.Movie?.slug}/${nextShow.sessionId}?step=inicio`
        : '/';
      setOverlayProps({
        title: 'Esta función ya no esta disponible.',
        message: 'Ya pasaron 20 minutos de haber empezado la función, intenta en otro horario',
        icon: <LockFilled className="text-5xl" />,
        visible: true,
        children: (
          <Link
            href={href}
            className="btn text-black"
            onClick={() => setTimeout(() => setOverlayProps({}), 500)}
          >
            {nextShow ? 'Siguiente función' : 'Ir a la cartelera'}
          </Link>
        ),
      });
    } else {
      setTimeout(() => {
        setTime(dayjs().tz(show?.Cinema?.timezone || 'America/Mazatlán'));
      }, 1000 * 60);
    }
  }, [time]);
  const title = show?.Movie.title;
  const cinema = show?.Cinema?.name;
  const date = dayjs(show?.schedule).format('LLLL');
  return (
    <Layout
      meta={{
        title: pageTitle,
        description: `Venta de boletos para ${title} en Citicinemas ${cinema} en ${date} `,
        ogImage: `${process.env.NEXT_PUBLIC_APP_URL}/api/og/movie/${show.Movie.slug}`,
        noIndex: true,
      }}
    >
      {currentStep === 'inicio' && (
        <ShowSchedules
          show={show}
          dates={dates}
          schedules={schedules}
          key={`${show?.sessionId}-schedules`}
        />
      )}
      {currentStep === 'pago' && <ShowSummary show={show} key={`${show?.sessionId}-summary`} />}
      <PurchaseFlow key={`${show?.sessionId}-flow`} show={show} step={currentStep} />
      <OverlayMessage {...overlayProps} />
      <RemainingTime />
    </Layout>
  );
};

export default PurchasePage;
